import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { PasswordChange } from "../../shared/components/password-change/password-change";
import { Stepper } from "../../shared/components/stepper/stepper";
import { Sidebar } from '../../core/services/sidebar';
import { PasswordField } from "../../shared/ui/password-field/password-field";
import { Button } from "../../shared/ui/button/button";
import { Popup } from '../../core/services/popup';
import { Header } from '../../core/services/header';

@Component({
  selector: 'app-change-password',
  imports: [PasswordChange, Stepper, CommonModule, TranslatePipe, PasswordField, Button, ReactiveFormsModule],
  templateUrl: './change-password.html',
  styleUrl: './change-password.css'
})
export class ChangePassword {
  fromPage: string | null = null;
  currentStep = 1;
  currentPasswordForm!: FormGroup;

  steps = [
    { label: 'FORGOT_PASSWORD.CREATE_NEW_PASSWORD' },
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sidebarService: Sidebar,
    private fb: FormBuilder,
    private popup: Popup,
    private headerService: Header
  ) { }

  ngOnInit() {
    // Capture "from" parameter dynamically
    this.route.queryParamMap.subscribe((params) => {
      this.fromPage = params.get('from');
      this.setupSteps();
    });

    this.headerService.updateConfig({
      showRightSection: false
    })
  }

  private setupSteps() {
    if (this.fromPage === 'settings') {
      // Two-step process
      this.steps = [
        { label: 'CHANGE_PASSWORD.CURRENT_PASSWORD' },
        { label: 'CHANGE_PASSWORD.NEW_PASSWORD' },
      ];

      // Initialize form for Step 1
      this.currentPasswordForm = this.fb.group({
        currentPassword: ['', [Validators.required, Validators.minLength(3)]],
      });

      this.sidebarService.setLinks([
        {
          label: 'SIDEBAR.CANCEL',
          route: '/settings',
          iconPath: 'assets/icons/cancel-blue.svg',
          variant: 'plain',
        },
      ]);
    } else {
      // Single-step process
      this.steps = [{ label: 'FORGOT_PASSWORD.CREATE_NEW_PASSWORD' }];

      this.sidebarService.setLinks([
        {
          label: 'SIDEBAR.CANCEL',
          route: `/${this.fromPage ?? 'login'}`,
          iconPath: 'assets/icons/cancel-blue.svg',
          variant: 'plain',
        },
      ]);
    }
  }

  handleStep1() {
    if (this.currentPasswordForm.invalid) return;

    // TODO: Validate current password via API

    // Move to Step 2
    this.currentStep = 2;

    this.sidebarService.setLinks([
      {
        label: 'SIDEBAR.PREVIOUS_SCREEN',
        action: () => this.goBackStep(),
        iconPath: 'assets/icons/arrow-blue.svg',
        variant: 'outlined',
      },
      {
        label: 'SIDEBAR.CANCEL',
        route: '/settings',
        iconPath: 'assets/icons/cancel-blue.svg',
        variant: 'plain',
      },
    ]);
  }

  handleConfirmClick(passwordData: { newPassword: string; confirmPassword: string }) {
    // console.log('Final Password Payload:', passwordData);

    // TODO: Call password update API

    if (this.fromPage === 'settings') {
      this.popup.show({
        message: 'CHANGE_PASSWORD.PASSWORD_CHANGED',
        type: 'success',
        position: 'bottom',
        icon: 'assets/icons/check-popup.svg',
      });
    }

    // Redirect after success
    const redirectTo = this.fromPage === 'login' ? '/accounts' : `/${this.fromPage ?? 'login'}`;
    this.router.navigate([redirectTo]);
  }

  goBackStep() {
    this.currentStep = 1;
    this.sidebarService.setLinks([
      {
        label: 'SIDEBAR.CANCEL',
        route: '/settings',
        iconPath: 'assets/icons/cancel-blue.svg',
        variant: 'plain',
      },
    ]);
  }

  get currentPasswordFormControl(): FormControl {
    return this.currentPasswordForm.get('currentPassword') as FormControl;
  }
}
